import React, { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";

/*
  PAGE: Study in New Zealand from Hyderabad - Complete Guide 2026
  ROUTE: Add <Route path="/study-in-new-zealand-from-hyderabad" element={<StudyInNZ />} />
  SEO TARGET: "study in New Zealand from Hyderabad", "New Zealand student visa consultant Hyderabad"
*/

// ─── Brand palette (matches App.js) ──────────────────────────────
const green      = "#0D4B2D"; // Deep Emerald Green – banners, H1/H2
const gold       = "#B8860B"; // Antique Gold – borders, badges, highlight numbers
const cream      = "#FAF8F3"; // Warm Cream – page background
const white      = "#FFFFFF"; // Pure White – cards, checklist, tables
const red        = "#C0392B"; // Crimson Red – CTA buttons
const redDark    = "#A93226"; // CTA hover
const text       = "#14241B"; // Dark Forest Charcoal – body text
const muted      = "#4A5A50"; // secondary text
const goldBorder = "rgba(184, 134, 11, 0.35)";
const goldSoft   = "rgba(184, 134, 11, 0.08)";
const goldShadow = "0 8px 24px rgba(184, 134, 11, 0.12)";

// ─── Styled components ────────────────────────────────────────────
const Page = styled.div`
  font-family: 'Fira Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color: ${text};
  background: ${cream};
`;

// Hero (banner)
const Hero = styled.section`
  background: ${green};
  color: ${white};
  border-bottom: 3px solid ${gold};
  padding: 64px 24px 56px;
  text-align: center;
`;
const Eyebrow = styled.p`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${gold};
  margin: 0 0 14px;
`;
const H1 = styled.h1`
  font-family: 'Fira Sans', sans-serif;
  font-size: clamp(1.75rem, 4vw, 2.8rem);
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: 0.5px;
  margin: 0 auto 16px;
  max-width: 700px;
  color: ${white};
`;
const HeroSub = styled.p`
  font-size: clamp(0.95rem, 2vw, 1.2rem);
  color: rgba(250, 248, 243, 0.85);
  max-width: 540px;
  margin: 0 auto 32px;
  line-height: 1.6;
  font-weight: 400;
`;
const CTABtn = styled(Link)`
  display: inline-block;
  background: ${red};
  color: ${white};
  font-family: 'Fira Sans', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  padding: 13px 32px;
  border-radius: 10px;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(192, 57, 43, 0.25);
  transition: background 0.18s, transform 0.18s;
  &:hover, &:focus {
    background: ${redDark};
    color: ${white};
    transform: translateY(-1px);
  }
`;

// Content wrapper
const Wrap = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 700px) { padding: 0 16px; }
`;

// Stat strip
const StatStrip = styled.div`
  background: ${white};
  border-bottom: 1px solid ${goldBorder};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
`;
const Stat = styled.div`
  padding: 28px 20px;
  text-align: center;
  border-right: 1px solid ${goldBorder};
  &:last-child { border-right: none; }
`;
const StatNum = styled.div`
  font-size: 1.9rem;
  font-weight: 900;
  color: ${gold};
  margin-bottom: 4px;
`;
const StatLabel = styled.div`
  font-size: 0.8rem;
  color: ${muted};
  font-weight: 500;
`;

// Section
const Section = styled.section`
  padding: 52px 0 44px;
  border-bottom: 1px solid ${goldBorder};
  &:last-of-type { border-bottom: none; }
`;
const H2 = styled.h2`
  font-family: 'Fira Sans', sans-serif;
  font-size: clamp(1.3rem, 3vw, 1.9rem);
  font-weight: 900;
  color: ${green};
  margin: 0 0 16px;
  line-height: 1.25;
`;
const H3 = styled.h3`
  font-family: 'Fira Sans', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${green};
  margin: 24px 0 8px;
`;
const P = styled.p`
  font-size: 1rem;
  line-height: 1.75;
  color: ${text};
  margin: 0 0 16px;
`;

// Info cards
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin: 24px 0;
`;
const Card = styled.div`
  background: ${white};
  border-radius: 12px;
  padding: 22px 20px;
  border: 1px solid ${goldBorder};
  border-top: 3px solid ${gold};
  box-shadow: ${goldShadow};
`;
const CardTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${green};
  margin-bottom: 8px;
`;
const CardValue = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${gold};
  margin-bottom: 4px;
`;
const CardNote = styled.div`
  font-size: 0.84rem;
  color: ${muted};
  line-height: 1.5;
`;

// Steps
const StepList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 20px 0;
  counter-reset: steps;
`;
const Step = styled.li`
  counter-increment: steps;
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  align-items: flex-start;
  &::before {
    content: counter(steps);
    background: ${gold};
    color: ${white};
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;
const StepContent = styled.div``;
const StepTitle = styled.div`
  font-weight: 700;
  color: ${green};
  margin-bottom: 4px;
  font-size: 1rem;
`;
const StepDesc = styled.div`
  font-size: 0.92rem;
  color: ${text};
  line-height: 1.6;
`;

// Checklist (document checklist box – pure white)
const CheckList = styled.ul`
  list-style: none;
  padding: 20px;
  margin: 16px 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  background: ${white};
  border: 1px solid ${goldBorder};
  border-radius: 12px;
  box-shadow: ${goldShadow};
`;
const CheckItem = styled.li`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 0.92rem;
  color: ${text};
  line-height: 1.5;
  &::before {
    content: "✓";
    background: ${green};
    color: ${white};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

// University table
const TableWrap = styled.div`
  overflow-x: auto;
  background: ${white};
  border: 1px solid ${goldBorder};
  border-radius: 12px;
  box-shadow: ${goldShadow};
  margin: 20px 0;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;
const Th = styled.th`
  background: ${green};
  color: ${white};
  padding: 12px 14px;
  text-align: left;
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.3px;
  border-bottom: 2px solid ${gold};
`;
const Td = styled.td`
  padding: 12px 14px;
  border-bottom: 1px solid ${goldBorder};
  color: ${text};
  vertical-align: top;
`;
const Tr = styled.tr`
  &:nth-child(even) { background: ${cream}; }
  &:last-child td { border-bottom: none; }
  &:hover { background: ${goldSoft}; }
`;

// FAQ
const FAQList = styled.div`
  background: ${white};
  border: 1px solid ${goldBorder};
  border-radius: 12px;
  box-shadow: ${goldShadow};
  padding: 0 20px;
`;
const FAQItem = styled.div`
  border-bottom: 1px solid ${goldBorder};
  &:last-child { border-bottom: none; }
`;
const FAQQuestion = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 18px 0;
  text-align: left;
  font-family: 'Fira Sans', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: ${green};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;
const FAQAnswer = styled.div`
  font-size: 0.93rem;
  line-height: 1.7;
  color: ${text};
  padding-bottom: 16px;
  display: ${({ open }) => open ? "block" : "none"};
`;

// Bottom CTA
const BottomCTA = styled.section`
  background: ${green};
  border-top: 3px solid ${gold};
  padding: 56px 24px;
  text-align: center;
`;

// ─── Component ────────────────────────────────────────────────────
export default function StudyInNZ() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  const faqs = [
    {
      q: "Can I study in New Zealand from Hyderabad without appearing for IELTS?",
      a: "In most cases, no. New Zealand universities require an English proficiency score - IELTS 6.0 for undergraduate programs and 6.5 for postgraduate. However, students who have completed their 10+2 or degree from an English-medium institution in India may qualify for a waiver at some universities. Our counselors assess your eligibility case by case."
    },
    {
      q: "How long does it take to get a New Zealand student visa?",
      a: "The standard processing time is 20 to 30 working days once you submit a complete application online. During peak intake periods (January and July), it can take slightly longer. We recommend applying at least 10 to 12 weeks before your course start date to allow buffer time for any additional document requests."
    },
    {
      q: "Can I work while studying in New Zealand?",
      a: "Yes. As of November 2025, eligible tertiary students on a Fee-Paying Student Visa can work up to 25 hours per week during semesters and full-time during university holidays. At current Hyderabad-to-NZD exchange rates, part-time work meaningfully offsets living costs."
    },
    {
      q: "What is the post-study work visa duration in New Zealand?",
      a: "It depends on your qualification level. Bachelor's degree graduates are eligible for up to 3 years. Master's and PhD graduates also qualify for up to 3 years. From late 2026, a new Short-Term Graduate Work Visa will offer 6 months of open work rights for Level 5 to 7 qualification holders."
    },
    {
      q: "What is the total cost of studying in New Zealand for students from Hyderabad?",
      a: "For a two-year Master's program, the total cost including tuition, accommodation, and living expenses typically falls between ₹40 and ₹65 lakhs depending on the university and city. Auckland is the most expensive; Dunedin and Hamilton are significantly more affordable. We help students build a realistic budget and explore scholarship options to reduce this."
    },
    {
      q: "Does KiwiOra help with partner and dependent visas for New Zealand?",
      a: "Yes, this is one of our core services. If you are a student heading to New Zealand, your spouse can apply for a Partner of Student Work Visa, which allows them to work full-time while you study. Dependent children under 18 can also apply for a student visa. We handle both applications alongside your primary student visa."
    },
    {
      q: "Why choose KiwiOra over a general study abroad consultant in Hyderabad?",
      a: "Most consultancies in Hyderabad treat New Zealand as one option among many. At KiwiOra, it is the only country we work with. That means our knowledge of INZ policies, university intakes, NZQA accreditation requirements, and post-study pathways is current and deep. We do not spread our attention across eight countries - we give it entirely to getting you to New Zealand."
    }
  ];

  return (
    <Page>
      {/* ── SEO meta tags - add these via react-helmet or your index.html ──
          <title>Study in New Zealand from Hyderabad - Complete Guide 2026 | KiwiOra</title>
          <meta name="description" content="Planning to study in New Zealand from Hyderabad? Get the complete 2026 guide - visa requirements, top universities, costs in rupees, scholarships, and post-study work rights. Free consultation from KiwiOra, Hyderabad's NZ-specialist." />
          <meta name="keywords" content="study in New Zealand from Hyderabad, New Zealand student visa Hyderabad, NZ education consultant Hyderabad, study abroad New Zealand India 2026" />
      */}

      {/* ── HERO ── */}
      <Hero>
        <Wrap>
          <Eyebrow>KiwiOra - Hyderabad's New Zealand Specialists</Eyebrow>
          <H1>Study in New Zealand from Hyderabad - Complete Guide 2026</H1>
          <HeroSub>
            Everything you need to know about universities, visa requirements,
            costs in rupees, scholarships, and post-study work rights -
            written by consultants who work exclusively with New Zealand.
          </HeroSub>
          <CTABtn to="/get-started">Book a Free Consultation</CTABtn>
        </Wrap>
      </Hero>

      {/* ── STAT STRIP ── */}
      <StatStrip>
        <Stat>
          <StatNum>8</StatNum>
          <StatLabel>NZ Universities - all globally ranked</StatLabel>
        </Stat>
        <Stat>
          <StatNum>25 hrs</StatNum>
          <StatLabel>Part-time work allowed per week</StatLabel>
        </Stat>
        <Stat>
          <StatNum>3 years</StatNum>
          <StatLabel>Post-study work visa (Masters/PhD)</StatLabel>
        </Stat>
        <Stat>
          <StatNum>100%</StatNum>
          <StatLabel>Online visa process - no paper applications</StatLabel>
        </Stat>
      </StatStrip>

      {/* ── INTRO ── */}
      <Section>
        <Wrap>
          <H2>Why Students from Hyderabad Are Choosing New Zealand in 2026</H2>
          <P>
            New Zealand does not get the same attention as Canada or Australia among students
            in Hyderabad, and that is precisely why it is worth a serious look. Every one of
            New Zealand's eight universities ranks in the top 3% globally. The visa process
            is fully online. Post-study work rights run up to three years. And unlike Canada,
            which has tightened study permit caps significantly, New Zealand's intake pipeline
            for Indian students remains open and predictable.
          </P>
          <P>
            For students from Hyderabad specifically, the time zone difference with New Zealand
            is only 6.5 hours during IST, making it easier for families to stay in contact.
            Direct flights from Hyderabad's Rajiv Gandhi International Airport via Singapore or
            Kuala Lumpur reach Auckland in under 14 hours. And New Zealand's Indian student
            community - concentrated in Auckland, Wellington, and Christchurch - is established
            enough that arriving students find networks quickly.
          </P>
          <P>
            This guide covers everything a student from Hyderabad needs to plan their New Zealand
            application in 2026: which universities to consider, what the visa actually requires,
            what things cost in rupees, what scholarship money is genuinely available, and what
            happens after you graduate.
          </P>
        </Wrap>
      </Section>

      {/* ── UNIVERSITIES ── */}
      <Section>
        <Wrap>
          <H2>Top New Zealand Universities for Indian Students</H2>
          <P>
            New Zealand has eight government-funded universities. All are NZQA-accredited,
            which means their qualifications are recognised by Immigration New Zealand for
            visa purposes and by employers worldwide. Here is a quick overview of each,
            with the subjects they are best known for among Indian students.
          </P>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>University</Th>
                  <Th>Location</Th>
                  <Th>Known for</Th>
                  <Th>Annual fees (approx.)</Th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["University of Auckland", "Auckland", "Business, Engineering, Medicine, Law", "₹22 – 32 lakhs"],
                  ["University of Otago", "Dunedin", "Health Sciences, Pharmacy, Research", "₹18 – 28 lakhs"],
                  ["Victoria University of Wellington", "Wellington", "Law, Public Policy, Architecture", "₹17 – 26 lakhs"],
                  ["University of Canterbury", "Christchurch", "Engineering, Science, Arts", "₹17 – 25 lakhs"],
                  ["Massey University", "Palmerston North / Auckland", "Agriculture, Business, Aviation", "₹16 – 24 lakhs"],
                  ["AUT", "Auckland", "Business, IT, Health Sciences", "₹16 – 24 lakhs"],
                  ["University of Waikato", "Hamilton", "Computing, Management, Law", "₹15 – 22 lakhs"],
                  ["Lincoln University", "Christchurch", "Agriculture, Environment, Commerce", "₹14 – 20 lakhs"],
                ].map(([uni, loc, known, fees]) => (
                  <Tr key={uni}>
                    <Td style={{ fontWeight: 700, color: green }}>{uni}</Td>
                    <Td>{loc}</Td>
                    <Td>{known}</Td>
                    <Td>{fees}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
          <P style={{ fontSize: "0.82rem", color: muted }}>
            Fees are approximate 2026 estimates for international students at Master's level.
            Undergraduate and PhD costs vary. Contact KiwiOra for a program-specific breakdown.
          </P>
        </Wrap>
      </Section>

      {/* ── VISA REQUIREMENTS ── */}
      <Section>
        <Wrap>
          <H2>New Zealand Student Visa Requirements for Indian Students - 2026</H2>
          <P>
            Most students from India apply for the Fee-Paying Student Visa, which covers any
            full-time program of more than three months at an NZQA-approved institution.
            Since 2024, all applications are submitted online through the Immigration New
            Zealand portal - there is no paper route.
          </P>

          <H3>Documents you will need</H3>
          <CheckList>
            {[
              "Offer of Place from an NZQA-accredited institution",
              "Valid passport with at least 6 months' validity",
              "Proof of funds - NZD 20,000 per year for living costs, plus first-year tuition",
              "English proficiency score - IELTS 6.0 (UG) or 6.5 (PG), PTE or TOEFL accepted",
              "Medical certificate (chest X-ray for stays over 12 months)",
              "Police clearance certificate for stays over 24 months",
              "Tuition fee payment receipt or confirmation",
              "Passport-size photographs meeting INZ specifications",
            ].map((item) => <CheckItem key={item}>{item}</CheckItem>)}
          </CheckList>

          <H3>Financial proof - what the numbers look like in rupees</H3>
          <P>
            Immigration New Zealand requires you to show funds covering your first-year
            tuition plus NZD 20,000 (approximately ₹10.4 lakhs at current rates) for living
            costs. For a Master's program with annual tuition of NZD 30,000, you would need
            to show approximately NZD 50,000 - around ₹26 lakhs - in accessible funds. This
            can be in your own account, your parents' account, fixed deposits, or a sanctioned
            education loan letter. Avoid large cash deposits with no paper trail, as INZ will
            query source of funds.
          </P>

          <H3>Processing time and when to apply</H3>
          <P>
            Standard visa processing takes 20 to 30 working days. New Zealand has two main
            intakes each year - February and July. We recommend beginning your university
            application 10 to 12 months before your intended start date, and your visa
            application at least 10 to 12 weeks before the course begins. You may apply up
            to 120 days in advance.
          </P>
        </Wrap>
      </Section>

      {/* ── COSTS ── */}
      <Section>
        <Wrap>
          <H2>Cost of Studying in New Zealand - What Students from Hyderabad Should Budget</H2>
          <P>
            New Zealand is not the cheapest destination, but it is meaningfully more
            affordable than the UK or the US for an equivalent qualification. Here is a
            realistic budget breakdown for a two-year Master's program.
          </P>
          <CardGrid>
            <Card>
              <CardTitle>Tuition - Master's</CardTitle>
              <CardValue>₹10 – 23 lakhs/year</CardValue>
              <CardNote>Depends on university and subject. Engineering and health sciences are at the upper end; arts and social sciences at the lower.</CardNote>
            </Card>
            <Card>
              <CardTitle>Living costs</CardTitle>
              <CardValue>₹10.4 lakhs/year</CardValue>
              <CardNote>INZ mandates NZD 20,000/year. Auckland costs more; Hamilton and Dunedin are significantly cheaper.</CardNote>
            </Card>
            <Card>
              <CardTitle>Accommodation</CardTitle>
              <CardValue>₹4 – 7 lakhs/year</CardValue>
              <CardNote>University hostels are convenient for year one. Shared housing in year two brings costs down considerably.</CardNote>
            </Card>
            <Card>
              <CardTitle>Visa fee</CardTitle>
              <CardValue>NZD 750 (~₹39,000)</CardValue>
              <CardNote>Plus IVL of NZD 100 and medical/police clearance costs. One-time at the start of your program.</CardNote>
            </Card>
            <Card>
              <CardTitle>Part-time work offset</CardTitle>
              <CardValue>₹3 – 5 lakhs/year</CardValue>
              <CardNote>At 25 hours/week, minimum wage in NZ (NZD 23.15/hr) earns approximately NZD 30,000/year - a meaningful offset.</CardNote>
            </Card>
            <Card>
              <CardTitle>PhD - a special case</CardTitle>
              <CardValue>₹3.9 – 4.2 lakhs/year</CardValue>
              <CardNote>Several NZ universities charge international PhD students at domestic rates - around NZD 7,500 to 8,000/year. One of the most cost-effective doctoral options globally.</CardNote>
            </Card>
          </CardGrid>
        </Wrap>
      </Section>

      {/* ── SCHOLARSHIPS ── */}
      <Section>
        <Wrap>
          <H2>Scholarships Available for Indian Students in 2026</H2>
          <P>
            Scholarship money for New Zealand is real but competitive. The window to apply is
            longer than most students realise - most awards require applications 10 to 12
            months before your intended intake. Starting early is not excessive; it is standard
            for the better awards.
          </P>

          <H3>Manaaki New Zealand Scholarships</H3>
          <P>
            Government-funded by the New Zealand Ministry of Foreign Affairs and Trade. Fully
            funded - covers tuition, a monthly living stipend, return airfare, and health
            insurance. Open to postgraduate and PhD applicants from eligible countries including
            India. Highly competitive; applications typically open 12 months before the intake.
          </P>

          <H3>University-specific scholarships</H3>
          <P>
            Most New Zealand universities offer merit scholarships for international students
            at postgraduate level, ranging from NZD 5,000 to NZD 15,000 per year. The
            University of Auckland's Faculty scholarships, Otago's International Excellence
            Scholarship, and Victoria's Vice-Chancellor's Award are among the most accessible
            for strong applicants. These do not fully fund your degree but meaningfully reduce
            the cost.
          </P>

          <H3>Research assistantships - PhD pathway</H3>
          <P>
            PhD students working under a supervisor with funded research projects often receive
            a stipend of NZD 25,000 to NZD 30,000 per year in addition to the domestic-rate
            tuition. This makes a New Zealand PhD one of the few doctoral programs where total
            cost to the student can approach zero for strong candidates in science, engineering,
            and agriculture.
          </P>
        </Wrap>
      </Section>

      {/* ── APPLICATION STEPS ── */}
      <Section>
        <Wrap>
          <H2>Step-by-Step: How to Apply to Study in New Zealand from Hyderabad</H2>
          <StepList>
            <Step>
              <StepContent>
                <StepTitle>Shortlist universities and programs</StepTitle>
                <StepDesc>Based on your academic background, field of study, and target city. Confirm the institution is NZQA-accredited - a non-accredited provider will result in an outright visa refusal regardless of your profile.</StepDesc>
              </StepContent>
            </Step>
            <Step>
              <StepContent>
                <StepTitle>Appear for IELTS, PTE, or TOEFL</StepTitle>
                <StepDesc>Minimum IELTS 6.0 for undergraduate, 6.5 for postgraduate, with no band below 5.5. Book your test 3 to 4 months before your application deadline to allow a resit if needed.</StepDesc>
              </StepContent>
            </Step>
            <Step>
              <StepContent>
                <StepTitle>Submit university applications and receive Offer of Place</StepTitle>
                <StepDesc>Most universities in New Zealand have rolling admissions, but February intake applications close around October and July intake applications close around April. A conditional offer requires you to meet specified academic conditions before it becomes unconditional.</StepDesc>
              </StepContent>
            </Step>
            <Step>
              <StepContent>
                <StepTitle>Arrange finances and pay tuition deposit</StepTitle>
                <StepDesc>Prepare your bank statements showing NZD 20,000 for living costs plus your tuition fee. Avoid large last-minute deposits. Pay the university's required deposit to convert your offer to unconditional.</StepDesc>
              </StepContent>
            </Step>
            <Step>
              <StepContent>
                <StepTitle>Complete medical examination and police clearance</StepTitle>
                <StepDesc>Book your chest X-ray with an INZ-approved panel physician in Hyderabad. Results must be less than 3 months old at the time of your visa application. Police clearance is needed for stays over 24 months.</StepDesc>
              </StepContent>
            </Step>
            <Step>
              <StepContent>
                <StepTitle>Lodge your visa application online</StepTitle>
                <StepDesc>Submit through the Immigration New Zealand portal with all required documents. Pay the NZD 750 application fee online. You can apply up to 120 days before your course start date.</StepDesc>
              </StepContent>
            </Step>
            <Step>
              <StepContent>
                <StepTitle>Receive visa decision and book flights</StepTitle>
                <StepDesc>Processing takes 20 to 30 working days. Once approved, arrange accommodation, book your flight, and attend KiwiOra's pre-departure briefing covering arrival, SIM cards, bank accounts, and IRD number registration.</StepDesc>
              </StepContent>
            </Step>
          </StepList>
        </Wrap>
      </Section>

      {/* ── POST STUDY ── */}
      <Section>
        <Wrap>
          <H2>Post-Study Work Rights and the Path to PR</H2>
          <P>
            The post-study work visa is one of the strongest reasons students from Hyderabad
            choose New Zealand over the UK, which has tighter post-study work restrictions,
            or Canada, which is unpredictable right now.
          </P>

          <H3>Post-Study Work Visa (PSWV)</H3>
          <P>
            After completing an eligible qualification at Level 7 or above, graduates can
            apply for an open PSWV allowing them to work for any employer in any job.
            Duration is tied to your qualification: Bachelor's and Master's graduates receive
            up to 3 years; PhD graduates receive up to 3 years. You can only be granted one
            PSWV in your lifetime, so applying for the full duration matters. Apply before
            your student visa expires - within 3 months of completing your program for most
            qualifications.
          </P>

          <H3>Short-Term Graduate Work Visa (from late 2026)</H3>
          <P>
            A new visa opening for Level 5 to 7 qualification holders who completed at least
            24 weeks of full-time study. Provides 6 months of open work rights for graduates
            who do not qualify for the full PSWV - useful for diploma holders looking to gain
            local work experience before transitioning to an Accredited Employer Work Visa.
          </P>

          <H3>Skilled Migrant Category and PR</H3>
          <P>
            The pathway from student to permanent resident follows: PSWV - gain skilled work
            experience in New Zealand - accumulate points under the Skilled Migrant Category
            (SMC) system - submit an Expression of Interest. For graduates in engineering,
            IT, healthcare, and construction - fields consistently on New Zealand's Green
            List - the timeline to PR can be as short as 3 to 4 years after graduation.
          </P>
        </Wrap>
      </Section>

      {/* ── WHY KIWIORA ── */}
      <Section>
        <Wrap>
          <H2>Why Hyderabad Students Work With KiwiOra</H2>
          <P>
            Most consultancies in Hyderabad handle 12 to 15 countries. New Zealand is
            typically one line on a services list between Norway and Oman. At KiwiOra,
            New Zealand is the only country we work with.
          </P>
          <P>
            That focus means our knowledge of Immigration New Zealand policy changes, NZQA
            accreditation status updates, university intake shifts, and post-study pathway
            revisions is current in a way that a generalist consultant's knowledge cannot be.
            When INZ changed work hour limits in November 2025, we updated every client
            already in process the same day. When PhD domestic tuition rates became available
            to international students at select universities, we knew which programs qualified
            before most consultancies in Hyderabad had updated their brochures.
          </P>
          <P>
            We handle Student Visas, Partner of Student Work Visas, Dependent Visas, and
            Post-Study Work Visas. Our consultation is free. There is no sales pitch - if
            New Zealand is not the right fit for your academic background and goals, we will
            tell you that directly in the first session.
          </P>
          <div style={{ marginTop: 24 }}>
            <CTABtn to="/get-started">
              Book Your Free Consultation
            </CTABtn>
          </div>
        </Wrap>
      </Section>

      {/* ── FAQ ── */}
      <Section>
        <Wrap>
          <H2>Frequently Asked Questions</H2>
          <FAQList>
          {faqs.map((faq, i) => (
            <FAQItem key={i}>
              <FAQQuestion onClick={() => toggle(i)}>
                <span>{faq.q}</span>
                <span style={{ fontSize: "1.3rem", color: gold, fontWeight: 700, flexShrink: 0 }}>
                  {openFaq === i ? "−" : "+"}
                </span>
              </FAQQuestion>
              <FAQAnswer open={openFaq === i}>{faq.a}</FAQAnswer>
            </FAQItem>
          ))}
          </FAQList>
        </Wrap>
      </Section>

      {/* ── BOTTOM CTA ──
      <BottomCTA>
        <Wrap>
          <h2 style={{
            fontFamily: "'Fira Sans', sans-serif",
            fontSize: "clamp(1.4rem, 3vw, 2rem)",
            fontWeight: 900,
            color: white,
            margin: "0 0 12px",
          }}>
            Ready to start your New Zealand application?
          </h2>
          <p style={{
            color: "rgba(250,248,243,0.8)",
            fontSize: "1rem",
            margin: "0 0 28px",
            lineHeight: 1.6,
          }}>
            Talk to a KiwiOra consultant in Hyderabad who works exclusively with New Zealand.
            Free consultation, no commitment.
          </p>
          <CTABtn to="/get-started">Book a Free Consultation</CTABtn>
        </Wrap>
      </BottomCTA> */}
    </Page>
  );
}